/**
 * lib/tonearm.ts — the geometry of the tonearm: a second of the track, the groove the needle is
 * in, and the arm's turn that puts it there.
 *
 * Task 20. `spec.md` «Пластинка», «Тонарм». Pure: millimetres of the deck and degrees, nothing of
 * the page. The deck is drawn at 1 unit = 1 mm of the SL-1210MK2's top plate, 453 × 353, and the
 * two points that matter were measured on the maker's photo from above
 * (`.omc/wiki/raw/prototip-vertushki.html`): the arm's pivot and the platter's spindle. The arm
 * is drawn at rest as in that photo, its stylus at {@link STYLUS_AT_REST}; playing any groove is
 * a turn of that one rigid drawing about the pivot — which keeps the headshell within a few
 * degrees of the groove all the way in, as a real arm's offset does.
 *
 * ## Time is a straight line across the grooves
 *
 * A record is cut from the outside in, so 0:00 is at the outer edge of the grooves and the end
 * of the track at the edge of the label. In between the needle moves at an even pace: a real
 * record's grooves are evenly pitched, and a slider-like mapping is what a hand putting the
 * needle down expects. Each record has grooves of its own ({@link GROOVES}): the big one at 33,
 * the small one at 45.
 *
 * ## The angle is a triangle
 *
 * Pivot, spindle and needle make a triangle whose three sides are known — the pivot to the
 * spindle, the arm's length, and the groove's radius — so the arm's direction for a groove is
 * the law of cosines (`armTurnFor`), and the groove under a turned arm is where its needle ends
 * up (`radiusAtTurn`, task 22).
 */

import type { Point } from './platter';
import type { RecordSize } from './records';

export type { RecordSize };

/**
 * The deck's box, in millimetres: the top plate, 453 × 353, and the strip of dust-cover hinges
 * above it. Every part of the drawing is placed in this box (`styles/deck.css`, `assets/deck.svg`);
 * a point of the plate — {@link PIVOT}, {@link SPINDLE} — is {@link DECK_MM.hinges} lower in it.
 */
export const DECK_MM = Object.freeze({ width: 453, height: 363, hinges: 10 });

/** The arm's pivot, in millimetres of the deck's top plate. */
export const PIVOT: Point = Object.freeze({ x: 385, y: 84 });

/** The platter's spindle — the centre of the record. */
export const SPINDLE: Point = Object.freeze({ x: 179, y: 175 });

/** The needle of the arm as it is drawn: on its rest, as in the photo. */
export const STYLUS_AT_REST: Point = Object.freeze({ x: 334.4, y: 316.6 });

/** From the pivot to the needle, in millimetres. */
export const ARM_LENGTH = Math.hypot(STYLUS_AT_REST.x - PIVOT.x, STYLUS_AT_REST.y - PIVOT.y);

/** Where each record's grooves begin (0:00) and end, as radii from the spindle, in millimetres. */
export const GROOVES: Readonly<
  Record<RecordSize, { readonly start: number; readonly end: number }>
> = Object.freeze({
  '33': Object.freeze({ start: 146, end: 60 }),
  '45': Object.freeze({ start: 82, end: 50 }),
});

const TO_SPINDLE = Math.hypot(PIVOT.x - SPINDLE.x, PIVOT.y - SPINDLE.y);
const TOWARD_SPINDLE = Math.atan2(SPINDLE.y - PIVOT.y, SPINDLE.x - PIVOT.x);
const AT_REST = Math.atan2(STYLUS_AT_REST.y - PIVOT.y, STYLUS_AT_REST.x - PIVOT.x);
const DEG = 180 / Math.PI;

/** The share of the track played, 0…1; unknown or nonsense is the start. */
function share(positionS: number, durationS: number): number {
  if (!(durationS > 0) || !Number.isFinite(positionS)) return 0;
  return Math.min(1, Math.max(0, positionS / durationS));
}

/** The groove the needle is in at `positionS` of a track `durationS` long, in millimetres. */
export function grooveAt(positionS: number, durationS: number, size: RecordSize): number {
  const { start, end } = GROOVES[size];
  const played = share(positionS, durationS);
  if (played === 0) return start;
  if (played === 1) return end;
  return start + (end - start) * played;
}

/** The second of the track under a needle in the groove at `radius`, within the track. */
export function timeAtRadius(radius: number, durationS: number, size: RecordSize): number {
  const { start, end } = GROOVES[size];
  if (!(durationS > 0)) return 0;
  const played = Math.min(1, Math.max(0, (start - radius) / (start - end)));
  return played * durationS;
}

/** How far the arm turns from its rest to put the needle in the groove at `radius`, in degrees. */
export function armTurnFor(radius: number): number {
  const cos = (ARM_LENGTH ** 2 + TO_SPINDLE ** 2 - radius ** 2) / (2 * ARM_LENGTH * TO_SPINDLE);
  const direction = TOWARD_SPINDLE - Math.acos(Math.min(1, Math.max(-1, cos)));
  return (direction - AT_REST) * DEG;
}

/** Where the needle is when the arm is turned `turnDeg` from its rest. */
export function stylusAt(turnDeg: number): Point {
  const direction = AT_REST + turnDeg / DEG;
  return {
    x: PIVOT.x + ARM_LENGTH * Math.cos(direction),
    y: PIVOT.y + ARM_LENGTH * Math.sin(direction),
  };
}

/** The groove under the needle of an arm turned `turnDeg`: its distance from the spindle. */
export function radiusAtTurn(turnDeg: number): number {
  const needle = stylusAt(turnDeg);
  return Math.hypot(needle.x - SPINDLE.x, needle.y - SPINDLE.y);
}

/*
 * ── Putting the needle down (task 22) ─────────────────────────────────────
 *
 * A needle carried past the record's outer edge lands in the first groove, 0:00; past the edge
 * of the label, in the last one — the last second of the track, not its end, so the drop plays
 * something instead of ending the track on the spot. And no further than the phone could load:
 * the same limit as the slider's (`engine/readings.ts`, «How far the slider may go»).
 */

/** The last groove is this far from the end of the track. */
export const LAST_GROOVE_S = 1;

/** The furthest second a needle may be put down at: the last groove, or the loaded part's end. */
export function lastSecond(durationS: number, limitS: number): number {
  if (!(durationS > 0)) return 0;
  const lastGroove = Math.max(0, durationS - LAST_GROOVE_S);
  return Math.max(0, Math.min(lastGroove, Number.isFinite(limitS) ? limitS : lastGroove));
}

/** How far the arm may be turned while it is carried: from the first groove to the last allowed. */
export function carryRange(
  durationS: number,
  limitS: number,
  size: RecordSize,
): { readonly from: number; readonly to: number } {
  return {
    from: armTurnFor(GROOVES[size].start),
    to: armTurnFor(grooveAt(lastSecond(durationS, limitS), durationS, size)),
  };
}

/** Seconds within this of an edge are the edge: the triangle's arithmetic leaves a trace there. */
const EDGE_S = 1e-6;

/** The second under a needle on an arm turned `turnDeg`, within where it may be put down. */
export function needleTime(
  turnDeg: number,
  durationS: number,
  limitS: number,
  size: RecordSize,
): number {
  const last = lastSecond(durationS, limitS);
  const time = timeAtRadius(radiusAtTurn(turnDeg), durationS, size);
  if (time <= EDGE_S) return 0;
  return time >= last - EDGE_S ? last : time;
}
